import { ApolloError } from '@apollo/client'
import { DEFAULT_COUNTRY_CODE } from 'components/Formik/InputPhone/country-codes-list'
import {
  INewCompanyContactValues,
  INewBusinessClientFormValues,
  ILogoAsset,
  IBusinessClientFiltersForm,
  INewBusinessClientUserFormValues
} from 'features/BusinessClients/types'
import {
  BusinessClientSuggestion,
  BusinessClientUser,
  ChangeBusinessClientUserBusinessClientInput,
  ChangeBusinessClientUserInput
} from 'gql/generated/graphql'
import {
  GTChangeBusinessClientContactInput,
  GTChangeBusinessClientInput,
  GTBusinessClient,
  GTBusinessClientVertical,
  GTCreateBusinessClientInput,
  GTBusinessClientsInput,
  GTBusinessClientVerticalInput,
  InputMaybe,
  GTChangeBusinessClientKreditSettingsInput,
  GTFrequencyUnit,
  GTFrequencyUnitInput,
  GTCompanySuggestion,
  GTChangeUserAccountDetailsInput
} from 'gql/types.generated'
import { isEmpty, pick } from 'lodash'
import { IntlShape } from 'react-intl'
import { sanitizePrice } from 'utils/math'
import { getDifference } from 'utils/object'
import { getQueryString } from 'utils/route'

const mapContactsToMutation = (contacts: INewCompanyContactValues[]): GTChangeBusinessClientContactInput[] => {
  return contacts.map((contact) => {
    return {
      id: contact.id,
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      secondaryEmail: contact.secondaryEmail || undefined,
      secondaryPhone: contact.secondaryPhone || undefined
    }
  })
}

const mapFormToEditMutation = (values: Partial<INewBusinessClientFormValues>): GTChangeBusinessClientInput => {
  const mappedData = {
    companyIDs: values.companies?.map((company) => company.id),
    emailDomains: values.emailDomains,
    shareCustomersData: values.shareCustomersData,
    kamUserID: values.kam?.id,
    logoPath: values.logo?.path,
    name: values.clientName
  }

  return mappedData
}

const mapFormToCreateMutation = (values: INewBusinessClientFormValues): GTCreateBusinessClientInput => {
  const { companies, emailDomains, /*kam,*/ clientName } = values
  const companyIDs = companies.map((company) => company.id)
  const kamUserID = values.kam?.id

  const mappedData = {
    companyIDs,
    contacts: values.clientContacts.map((contact) => ({
      email: contact.email,
      name: contact.name,
      phone: contact.phone,
      secondaryEmail: contact.secondaryEmail || undefined,
      secondaryPhone: contact.secondaryPhone || undefined
    })),
    emailDomains,
    shareCustomersData: values.shareCustomersData || false,
    kamUserID,
    logoPath: (values.logo as ILogoAsset).path || undefined,
    name: clientName
  }

  return mappedData
}
export const getInitialBusinessClientValues = (company?: GTCompanySuggestion): INewBusinessClientFormValues => {
  return {
    logo: {
      name: '',
      path: ''
    },
    emailDomains: [],
    clientName: '',
    clientContacts: [
      {
        name: '',
        phone: '',
        secondaryPhone: '',
        email: '',
        secondaryEmail: ''
      }
    ],
    shareCustomersData: false,
    companies: company
      ? [
          {
            id: company.id,
            name: company.name,
            vertical: company.vertical as unknown as GTBusinessClientVertical
          }
        ]
      : [],
    verticals: company ? ([company.vertical] as unknown as GTBusinessClientVertical[]) : [],
    kreditsSettings: {
      categories: [
        {
          name: '',
          frequency: {
            unit: GTFrequencyUnit.Month,
            value: '1'
          },
          amount: ''
        }
      ]
    },
    physicalSims: [],
    credits: []
  }
}

export const DEFAULT_KREDITS_SETTINGS_VALUES: INewBusinessClientFormValues['kreditsSettings'] = {
  categories: [
    {
      isNew: true,
      name: '',
      frequency: {
        unit: GTFrequencyUnit.Month,
        value: '1'
      },
      amount: ''
    }
  ]
}

const mapResponseToForm = (values: GTBusinessClient): INewBusinessClientFormValues => {
  const mappedData: INewBusinessClientFormValues = {
    clientName: values.name,
    logo: {
      name: values.logo?.name ?? '',
      path: values.logo?.path ?? ''
    },
    emailDomains: values.emailDomains,
    shareCustomersData: values.shareCustomersData,
    clientContacts: values.contacts.map((contact) => ({
      ...contact,
      secondaryPhone: contact.secondaryPhone ?? '',
      secondaryEmail: contact.secondaryEmail ?? ''
    })),
    kam: values.kamUser ?? {
      id: '',
      firstName: '',
      lastName: ''
    },
    companies: values.companies,
    verticals: values.companies.map((company) => company.vertical),
    kreditsSettings: values.kreditsSettings
      ? {
          categories: values.kreditsSettings.categories.map((category) => ({
            name: category.name.toString(),
            amount: (category.amount / 100).toString(),
            frequency: {
              unit: category.frequency.unit,
              value: category.frequency.value.toString()
            }
          }))
        }
      : DEFAULT_KREDITS_SETTINGS_VALUES,
    physicalSims:
      values.processedMobileLineFiles?.map((file) => ({
        date: new Date(file.processedAt),
        addedBy: file.processedBy?.firstName + ' ' + (file.processedBy?.lastName ?? ''),
        file: '',
        filePath: '',
        id: file.id,
        amount: file.processedAmount
      })) || [],
    credits:
      values.kreditDeposits
        ?.map((deposit) => ({
          id: deposit.id,
          executedBy: deposit.user.firstName + ' ' + (deposit.user.lastName ?? ''),
          date: new Date(deposit.createdAt),
          filePath: '',
          file: '',
          amount: deposit.amount.toString()
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime()) || []
  }
  return mappedData
}

export const mapSearchToFiltersForm = (
  queries: Record<string, string | undefined>,
  { businessClient }: { businessClient: BusinessClientSuggestion | null }
): IBusinessClientFiltersForm => {
  return {
    businessClient: businessClient || { id: queries.businessClientId || '', name: '' },
    status: queries.status || '',
    vertical: queries.vertical || ''
  }
}

export const mapFiltersToQueryVariables = (filters: IBusinessClientFiltersForm): GTBusinessClientsInput => {
  return {
    id: filters.businessClient.id || undefined,
    isActive: filters.status === 'active' ? true : filters.status === 'inactive' ? false : undefined,
    vertical: (filters.vertical as InputMaybe<GTBusinessClientVerticalInput>) || undefined
  }
}

export const mapFiltersToSearchQueryString = (filters: IBusinessClientFiltersForm): string => {
  return getQueryString({
    businessClientId: filters.businessClient.id,
    status: filters.status,
    vertical: filters.vertical
  })
}

export const mapFormValuesToKreditSettingsMutation = (
  values: Partial<INewBusinessClientFormValues['kreditsSettings']>,
  clientId: string
): GTChangeBusinessClientKreditSettingsInput => {
  const kreditsSettings: INewBusinessClientFormValues['kreditsSettings'] =
    values as INewBusinessClientFormValues['kreditsSettings']
  const data: GTChangeBusinessClientKreditSettingsInput = {
    siteIDs: [],
    id: clientId,
    categories: kreditsSettings.categories.map((category) => ({
      name: Number(category.name),
      amount: sanitizePrice(Number(category.amount) * 100, true),
      frequency: {
        unit: category.frequency.unit as unknown as GTFrequencyUnitInput,
        value: Number(category.frequency.value)
      }
    }))
  }

  return data
}

export const businessClientFormUtils = {
  mapContactsToMutation,
  mapFormToEditMutation,
  mapResponseToForm,
  mapFormToCreateMutation,
  mapFormValuesToKreditSettingsMutation
}

const mapBusinessClientUserResponseToForm = (user: BusinessClientUser): INewBusinessClientUserFormValues => {
  return {
    hasAdminRole: user.hasAdminRole,
    businessClient: user.businessClient
      ? {
          id: user.businessClient.id,
          name: user.businessClient.name
        }
      : null,
    businessSite: user.businessSite
      ? {
          id: user.businessSite.id,
          name: user.businessSite.name
        }
      : null,
    firstName: user.userAccount.firstName || '',
    lastName: user.userAccount.lastName || '',
    phone: user.userAccount.phoneNumber?.number || '',
    email: user.userAccount.email || '',
    title: user.title || ''
  }
}

interface IBusinessClientUserMutationsInput {
  userAccountInput?: GTChangeUserAccountDetailsInput
  businessClientUserInput?: ChangeBusinessClientUserInput
  businessClientUserBusinessClientInput?: ChangeBusinessClientUserBusinessClientInput
}
const mapBusinessClientUserFormToUpdateMutation = (
  formValues: INewBusinessClientUserFormValues,
  user: BusinessClientUser
): {
  businessClientUserBusinessClientInput?: ChangeBusinessClientUserBusinessClientInput
  userAccountInput?: GTChangeUserAccountDetailsInput
  businessClientUserInput?: ChangeBusinessClientUserInput
} => {
  const oldUser = mapBusinessClientUserResponseToForm(user)
  const userDifference = getDifference(formValues, oldUser)
  const mutationsInput: IBusinessClientUserMutationsInput = {
    userAccountInput: undefined,
    businessClientUserInput: undefined,
    businessClientUserBusinessClientInput: undefined
  }

  if (!isEmpty(pick(userDifference, ['businessClient']))) {
    mutationsInput.businessClientUserBusinessClientInput = {
      id: user.id,
      businessClientID: formValues.businessClient?.id as string,
      businessSiteID: formValues.businessSite?.id || null
    }
  }

  if (!isEmpty(pick(userDifference, ['firstName', 'lastName', 'phone', 'email']))) {
    const isPhoneModified = !!userDifference.phone
    const isNameModified = !!userDifference.firstName || !!userDifference.lastName
    mutationsInput.userAccountInput = {
      id: user.userAccount.id,
      firstName: isNameModified ? formValues.firstName : null,
      lastName: isNameModified ? formValues.lastName : null,
      phoneNumber: isPhoneModified
        ? {
            number: formValues.phone,
            countryCode: DEFAULT_COUNTRY_CODE
          }
        : null,
      email: userDifference.email || null
    }
  }

  if (!isEmpty(pick(userDifference, ['title', 'businessSite', 'hasAdminRole']))) {
    mutationsInput.businessClientUserInput = {
      userAccountID: user.userAccount.id,
      title: formValues.title,
      businessSiteID: formValues.businessSite?.id || null,
      hasAdminRole: userDifference.hasAdminRole ?? null
    }
  }
  return mutationsInput
}

export const hasCSVError = (error?: ApolloError | string): boolean => {
  if (typeof error === 'string') {
    return false
  }
  //TODO: https://kolors.atlassian.net/browse/NA-18641
  return false
}
export const getPhysicalSimCSVErrors = (error: ApolloError, intl: IntlShape): string => {
  //TODO: https://kolors.atlassian.net/browse/NA-18641
  return intl.formatMessage({
    id: 'physicalSimsCSVForm.form.error',
    defaultMessage: 'Data in the file does not have the right format. Try again'
  })
}

export const businessClientUserFormUtils = {
  mapResponseToForm: mapBusinessClientUserResponseToForm,
  mapFormToUpdateMutation: mapBusinessClientUserFormToUpdateMutation
}
